'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Building2,
  CalendarCheck,
  CheckSquare,
  BookOpen,
  FileUp,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  Zap,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, group: 'main' },
  { id: 'nav-events', label: 'Events', href: '/events-list', icon: CalendarDays, badge: 3, group: 'main' },
  { id: 'nav-contacts', label: 'Contacts', href: '/contacts-list', icon: Users, badge: 247, group: 'main' },
  { id: 'nav-companies', label: 'Companies', href: '/companies', icon: Building2, group: 'main' },
  { id: 'nav-meetings', label: 'Meetings', href: '/meetings', icon: CalendarCheck, badge: 4, group: 'activity' },
  { id: 'nav-tasks', label: 'Tasks', href: '/tasks', icon: CheckSquare, badge: 12, group: 'activity' },
  { id: 'nav-content', label: 'Content Library', href: '/content-library', icon: BookOpen, group: 'activity' },
  { id: 'nav-import', label: 'Import', href: '/import', icon: FileUp, group: 'tools' },
  { id: 'nav-reports', label: 'Reports', href: '/reports', icon: BarChart3, group: 'tools' },
  { id: 'nav-settings', label: 'Settings', href: '/settings', icon: Settings, group: 'tools' },
];

const groupLabels: Record<string, string> = {
  main: 'Workspace',
  activity: 'Pipeline',
  tools: 'Tools',
};

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);

  const groups = ['main', 'activity', 'tools'];

  return (
    <aside
      className="relative flex flex-col h-screen bg-white border-r border-border shrink-0 transition-all duration-300 ease-in-out"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-3 py-4 border-b border-border overflow-hidden">
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-base text-foreground whitespace-nowrap tracking-tight">
            ShowCRM
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-thin">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          return (
            <div key={`group-${group}`} className="mb-1">
              {!collapsed && (
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                  {groupLabels[group]}
                </p>
              )}
              {collapsed && group !== 'main' && (
                <div className="mx-3 my-2 border-t border-border" />
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`sidebar-link mx-1 ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0 w-12 mx-auto' : ''}`}
                  >
                    <Icon size={18} strokeWidth={1.8} className="shrink-0" />
                    {!collapsed && (
                      <span className="flex-1 truncate">{item.label}</span>
                    )}
                    {!collapsed && item.badge !== undefined && (
                      <span className="ml-auto text-[10px] font-semibold font-mono bg-muted text-muted-foreground px-1.5 py-0.5 rounded-md min-w-[20px] text-center">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {collapsed && item.badge !== undefined && item.badge > 0 && (
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Notification banner */}
      {!collapsed && (
        <div className="mx-3 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Bell size={14} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-800">12 tasks overdue</p>
              <p className="text-[11px] text-amber-700 mt-0.5">NRA Show 2026 in 18 days</p>
            </div>
          </div>
        </div>
      )}

      {/* User + collapse */}
      <div className="border-t border-border">
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-3 py-3">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-[11px] font-semibold text-primary">JM</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">James Mitchell</p>
              <p className="text-[10px] text-muted-foreground truncate">Admin</p>
            </div>
            <Zap size={13} className="text-amber-500 shrink-0" />
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center py-2.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 border-t border-border"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}